import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { REACTIONS } from "@/pages/Converse";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Users as UsersIcon, ArrowRight } from "lucide-react";

const TINT_CHIP = {
  slate: "bg-slate-100 text-slate-700",
  emerald: "bg-emerald-50 text-emerald-700",
  blue: "bg-blue-50 text-blue-700",
  red: "bg-red-50 text-red-700",
  teal: "bg-teal-50 text-teal",
  amber: "bg-amber-50 text-amber-700",
  green: "bg-green-50 text-green-700",
  gold: "bg-gradient-to-br from-yellow-200 to-amber-300 text-amber-900",
  purple: "bg-gradient-to-br from-purple-100 to-indigo-200 text-purple-800",
};

export default function TrendingStrip() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/posts/trending?days=7&limit=3")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]));
  }, []);

  if (!posts || posts.length === 0) return null;

  return (
    <section data-testid="trending-strip">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="et-label mb-1.5 inline-flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-teal" />
            Trending on Converse
          </div>
          <h2 className="font-serif text-2xl text-navy">Most-reacted this week</h2>
        </div>
        <Link
          to="/converse"
          className="text-sm font-medium text-teal hover:underline inline-flex items-center gap-1"
          data-testid="trending-view-all"
        >
          Open Converse <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {posts.map((p) => (
          <TrendingCard key={p.id} post={p} />
        ))}
      </div>
    </section>
  );
}

function TrendingCard({ post }) {
  // Top 3 reactions present on this post
  const top = REACTIONS
    .map((r) => ({ ...r, n: post.reactions?.[r.kind] || 0 }))
    .filter((r) => r.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, 3);

  const total = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);

  return (
    <Link
      to="/converse"
      className="et-card p-5 block hover:border-teal transition group"
      data-testid={`trending-card-${post.id}`}
    >
      <header className="flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {post.author_avatar_url
            ? <img src={post.author_avatar_url} alt="" className="h-full w-full object-cover" />
            : <UsersIcon className="h-3.5 w-3.5 text-slate-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-navy truncate group-hover:text-teal">
            {post.author_name}
          </div>
        </div>
        {post.author_tier === "premium" && (
          <Badge className="bg-teal text-white text-[9px]">Premium</Badge>
        )}
      </header>

      <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-3">
        {post.body || <span className="italic text-slate-400">[attachment]</span>}
      </p>

      {post.attachment_url && post.attachment_kind === "image" && (
        <img
          src={post.attachment_url}
          alt=""
          className="mt-3 rounded-md border border-slate-200 h-32 w-full object-cover bg-slate-50"
        />
      )}

      <footer className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 flex-wrap">
          {top.map(({ kind, icon: I, tint, n }) => (
            <span
              key={kind}
              className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${TINT_CHIP[tint]}`}
            >
              <I className="h-3 w-3" />
              {n}
            </span>
          ))}
        </div>
        <span className="text-[11px] text-slate-500 font-medium whitespace-nowrap">
          {total} signal{total === 1 ? "" : "s"}
        </span>
      </footer>
    </Link>
  );
}
